import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CalendarSection = () => {
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  // Mock available time slots for the next few days
  const availableSlots = [
    {
      date: '2025-01-08',
      displayDate: 'Wed, Jan 8',
      slots: [
        { time: '10:00 AM', available: true },
        { time: '2:00 PM', available: true },
        { time: '4:00 PM', available: false }
      ]
    },
    {
      date: '2025-01-09',
      displayDate: 'Thu, Jan 9',
      slots: [
        { time: '9:00 AM', available: true },
        { time: '11:00 AM', available: true },
        { time: '3:00 PM', available: true }
      ]
    },
    {
      date: '2025-01-10',
      displayDate: 'Fri, Jan 10',
      slots: [
        { time: '10:00 AM', available: false },
        { time: '1:00 PM', available: true },
        { time: '3:30 PM', available: true }
      ]
    }
  ];

  const meetingTypes = [
    {
      id: 'coffee-chat',
      title: '15-min Coffee Chat',
      duration: '15 minutes',
      description: 'Quick introduction and project overview',
      icon: 'Coffee',
      color: 'bg-accent'
    },
    {
      id: 'consultation',
      title: 'Project Consultation',
      duration: '30 minutes',
      description: 'Detailed discussion about your project needs',
      icon: 'MessageSquare',
      color: 'bg-success'
    },
    {
      id: 'technical-review',
      title: 'Technical Review',
      duration: '45 minutes',
      description: 'Code review, architecture discussion, or technical audit',
      icon: 'Code2',
      color: 'bg-warning'
    }
  ];

  const handleTimeSlotSelect = (date, time) => {
    setSelectedDate(date);
    setSelectedTimeSlot(time);
  };

  const handleBooking = () => {
    // Simulate booking process
    alert(`Booking confirmed for ${selectedDate} at ${selectedTimeSlot}. You'll receive a calendar invite shortly!`);
    setSelectedDate(null);
    setSelectedTimeSlot(null);
  };

  return (
    <section id="calendar-section" className="py-20 bg-background">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Schedule a Call
          </h2>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto">
            Book a time that works for you. All meetings are conducted via Google Meet or Zoom.
          </p>
        </div>

        {/* Meeting Types */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {meetingTypes?.map((type) => (
            <div key={type?.id} className="bg-card border border-border rounded-xl p-6 hover:shadow-medium transition-all duration-300">
              <div className={`w-12 h-12 ${type?.color} rounded-lg flex items-center justify-center mb-4`}>
                <Icon name={type?.icon} size={24} color="white" />
              </div>
              <h3 className="text-lg font-bold text-text-primary mb-2">
                {type?.title}
              </h3>
              <p className="text-accent font-semibold mb-2">
                {type?.duration}
              </p>
              <p className="text-text-secondary text-sm">
                {type?.description}
              </p>
            </div>
          ))}
        </div>

        {/* Calendar Interface */}
        <div className="bg-card border border-border rounded-2xl p-8 shadow-soft">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Available Dates */}
            <div>
              <h3 className="text-xl font-bold text-text-primary mb-6 flex items-center">
                <Icon name="Calendar" size={24} className="mr-3 text-accent" />
                Available Dates
              </h3>
              
              <div className="space-y-4">
                {availableSlots?.map((day) => (
                  <div key={day?.date} className="border border-border rounded-lg p-4">
                    <h4 className="font-semibold text-text-primary mb-3">
                      {day?.displayDate}
                    </h4>
                    <div className="grid grid-cols-3 gap-2">
                      {day?.slots?.map((slot) => (
                        <button
                          key={slot?.time}
                          onClick={() => slot?.available && handleTimeSlotSelect(day?.displayDate, slot?.time)}
                          disabled={!slot?.available}
                          className={`p-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                            selectedDate === day?.displayDate && selectedTimeSlot === slot?.time
                              ? 'bg-accent text-accent-foreground'
                              : slot?.available
                              ? 'bg-muted hover:bg-accent/10 text-text-primary hover:text-accent' :'bg-muted/50 text-text-secondary cursor-not-allowed opacity-50'
                          }`}
                        >
                          {slot?.time}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Booking Summary */}
            <div>
              <h3 className="text-xl font-bold text-text-primary mb-6 flex items-center">
                <Icon name="Clock" size={24} className="mr-3 text-accent" />
                Booking Summary
              </h3>

              {selectedDate && selectedTimeSlot ? (
                <div className="bg-accent/5 border border-accent/20 rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <Icon name="CheckCircle" size={24} className="text-success" />
                    <span className="font-semibold text-text-primary">Time Selected</span>
                  </div>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Date:</span>
                      <span className="font-semibold text-text-primary">{selectedDate}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Time:</span>
                      <span className="font-semibold text-text-primary">{selectedTimeSlot} EST</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Duration:</span>
                      <span className="font-semibold text-text-primary">30 minutes</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Platform:</span>
                      <span className="font-semibold text-text-primary">Google Meet</span>
                    </div>
                  </div>

                  <Button
                    variant="default"
                    size="lg"
                    fullWidth
                    iconName="Calendar"
                    iconPosition="left"
                    onClick={handleBooking}
                    className="bg-accent hover:bg-hover text-accent-foreground"
                  >
                    Confirm Booking
                  </Button>
                </div>
              ) : (
                <div className="bg-muted/30 border border-border rounded-lg p-6 text-center">
                  <Icon name="Calendar" size={48} className="text-text-secondary mx-auto mb-4" />
                  <p className="text-text-secondary">
                    Select a date and time from the available slots to book your meeting.
                  </p>
                </div>
              )}

              {/* Meeting Details */}
              <div className="mt-8 p-4 bg-muted/30 rounded-lg">
                <h4 className="font-semibold text-text-primary mb-3 flex items-center">
                  <Icon name="Info" size={16} className="mr-2 text-accent" />
                  What to Expect
                </h4>
                <ul className="space-y-2 text-sm text-text-secondary">
                  <li className="flex items-start">
                    <Icon name="Check" size={14} className="text-success mr-2 mt-0.5" />
                    Calendar invite with meeting link
                  </li>
                  <li className="flex items-start">
                    <Icon name="Check" size={14} className="text-success mr-2 mt-0.5" />
                    Preparation email with agenda
                  </li>
                  <li className="flex items-start">
                    <Icon name="Check" size={14} className="text-success mr-2 mt-0.5" />
                    Follow-up summary and next steps
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Alternative Contact */}
        <div className="mt-12 text-center">
          <p className="text-text-secondary mb-4">
            Don't see a time that works? Need to schedule outside business hours?
          </p>
          <Button
            variant="outline"
            iconName="Mail"
            iconPosition="left"
            onClick={() => document.getElementById('contact-forms')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Send a Custom Request
          </Button>
        </div>
      </div>
    </section>
  );
};

export default CalendarSection;